import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, Truck, Clock, Info, ArrowRight } from 'lucide-react';

// Pricing matches the admin panel calculator (keep in sync)
const pricingData = {
  'same-day': {
    'Small Van': { range: [20, 80], perMile: 1.45 },
    'SWB': { range: [30, 90], perMile: 1.75 },
    'MWB': { range: [40, 110], perMile: 1.85 },
    'XLWB': { range: [40, 120], perMile: 2.00 },
    'Luton': { range: [60, 140], perMile: 2.30 },
  },
  'urgent': {
    'Small Van': { range: [40, 100], perMile: 2.00 },
    'SWB': { range: [50, 110], perMile: 2.20 },
    'MWB': { range: [60, 120], perMile: 2.30 },
    'XLWB': { range: [70, 120], perMile: 2.50 },
    'Luton': { range: [70, 160], perMile: 2.80 },
  },
};

const vehicleOptions = [
  { value: 'Small Van', label: 'Small Van — up to 600kg' },
  { value: 'SWB', label: 'Short Wheelbase (SWB) — up to 800kg' },
  { value: 'MWB', label: 'Medium Wheelbase (MWB) — up to 900kg' },
  { value: 'XLWB', label: 'XL Wheelbase (XLWB) — up to 1,200kg' },
  { value: 'Luton', label: 'Luton Van — up to 1,000kg (tail-lift)' },
];

export const PriceCalculator = ({ onBookNow }) => {
  const [distance, setDistance] = useState('');
  const [vehicle, setVehicle] = useState('Small Van');
  const [deliveryType, setDeliveryType] = useState('same-day');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCalculate = () => {
    setError('');
    setResult(null);

    const d = parseFloat(distance);
    if (!d || d <= 0) {
      setError('Please enter a valid distance in miles.');
      return;
    }
    if (d > 500) {
      setError('Distance cannot exceed 500 miles. Please call us for long-haul quotes: 01344 508095.');
      return;
    }

    const pricing = pricingData[deliveryType][vehicle];
    const [minBase, maxBase] = pricing.range;

    if (d <= 60) {
      // 0-60 miles: show a price range
      const vatMin = minBase * 0.20;
      const vatMax = maxBase * 0.20;
      setResult({
        type: 'range',
        distance: d,
        vehicle,
        deliveryType,
        subtotalMin: minBase,
        subtotalMax: maxBase,
        vatMin,
        vatMax,
        totalMin: minBase + vatMin,
        totalMax: maxBase + vatMax,
      });
    } else {
      // 60-500 miles: exact per-mile price
      const subtotal = +(d * pricing.perMile).toFixed(2);
      const vat = +(subtotal * 0.20).toFixed(2);
      const total = +(subtotal + vat).toFixed(2);
      setResult({
        type: 'exact',
        distance: d,
        vehicle,
        deliveryType,
        perMileRate: pricing.perMile,
        subtotal,
        vat,
        total,
      });
    }
  };

  const handleReset = () => {
    setDistance('');
    setVehicle('Small Van');
    setDeliveryType('same-day');
    setResult(null);
    setError('');
  };

  return (
    <Card className="shadow-2xl border-2 border-emerald-200 overflow-hidden" data-testid="customer-price-calculator">
      <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 p-6 text-white">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Calculator className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-1">Instant Price Estimate</h2>
            <p className="text-emerald-50 text-sm">
              Get an indicative price in seconds — VAT included. Book below to confirm your final quote.
            </p>
          </div>
        </div>
      </div>

      <CardContent className="p-6 sm:p-8 space-y-6 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Distance */}
          <div className="space-y-2">
            <Label htmlFor="calc-distance" className="text-sm font-semibold text-slate-700">
              Distance (miles) *
            </Label>
            <Input
              id="calc-distance"
              type="number"
              min="1"
              max="500"
              step="0.1"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              placeholder="e.g. 15"
              className="h-12 text-lg"
              data-testid="calc-distance-input"
            />
            <p className="text-xs text-slate-500">
              Enter the total one-way distance from pickup to drop-off.
            </p>
          </div>

          {/* Vehicle */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Vehicle Type *</Label>
            <Select value={vehicle} onValueChange={setVehicle}>
              <SelectTrigger className="h-12" data-testid="calc-vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {vehicleOptions.map((v) => (
                  <SelectItem key={v.value} value={v.value} data-testid={`calc-vehicle-${v.value}`}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Delivery type toggle */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Delivery Speed *</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setDeliveryType('same-day')}
              data-testid="calc-type-same-day"
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                deliveryType === 'same-day'
                  ? 'border-emerald-500 bg-emerald-50 shadow-md'
                  : 'border-slate-200 bg-white hover:border-emerald-300'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <Clock className="h-4 w-4 text-emerald-600" />
                <span className="font-semibold text-slate-900">Same-day / Next-day</span>
              </div>
              <p className="text-xs text-slate-600">1–12 hour delivery window</p>
            </button>

            <button
              type="button"
              onClick={() => setDeliveryType('urgent')}
              data-testid="calc-type-urgent"
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                deliveryType === 'urgent'
                  ? 'border-orange-500 bg-orange-50 shadow-md'
                  : 'border-slate-200 bg-white hover:border-orange-300'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <Truck className="h-4 w-4 text-orange-600" />
                <span className="font-semibold text-slate-900">Urgent / Express</span>
              </div>
              <p className="text-xs text-slate-600">0–6 hour priority handling</p>
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" data-testid="calc-error">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            size="lg"
            className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold"
            data-testid="calc-calculate-btn"
          >
            <Calculator className="h-5 w-5 mr-2" />
            Calculate Price
          </Button>
          <Button
            onClick={handleReset}
            variant="outline"
            size="lg"
            className="sm:w-auto"
            data-testid="calc-reset-btn"
          >
            Reset
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div
            className="mt-4 p-5 sm:p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300"
            data-testid="calc-result"
          >
            <h3 className="text-lg font-bold text-emerald-900 mb-4 flex items-center">
              <Calculator className="h-5 w-5 mr-2" />
              Your Estimated Price
            </h3>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <span className="text-slate-600">Distance:</span>
                <span className="ml-2 font-semibold text-slate-900">{result.distance} miles</span>
              </div>
              <div>
                <span className="text-slate-600">Vehicle:</span>
                <span className="ml-2 font-semibold text-slate-900">{result.vehicle}</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-600">Service:</span>
                <span className="ml-2 font-semibold text-slate-900">
                  {result.deliveryType === 'same-day' ? 'Same-day / Next-day (1–12h)' : 'Urgent (0–6h)'}
                </span>
              </div>
              {result.type === 'exact' && (
                <div className="col-span-2">
                  <span className="text-slate-600">Rate:</span>
                  <span className="ml-2 font-semibold text-slate-900">£{result.perMileRate}/mile</span>
                </div>
              )}
            </div>

            {result.type === 'range' ? (
              <div className="bg-white p-4 rounded-lg border border-emerald-200 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal range:</span>
                  <span className="font-semibold text-slate-900">
                    £{result.subtotalMin.toFixed(2)} – £{result.subtotalMax.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">VAT (20%):</span>
                  <span className="font-semibold text-slate-900">
                    £{result.vatMin.toFixed(2)} – £{result.vatMax.toFixed(2)}
                  </span>
                </div>
                <div className="pt-2 border-t-2 border-emerald-300 flex justify-between items-center">
                  <span className="text-base font-bold text-emerald-900">Total (inc. VAT):</span>
                  <span className="text-2xl sm:text-3xl font-extrabold text-emerald-700" data-testid="calc-total">
                    £{result.totalMin.toFixed(2)} – £{result.totalMax.toFixed(2)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-white p-4 rounded-lg border border-emerald-200 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-semibold text-slate-900">£{result.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">VAT (20%):</span>
                  <span className="font-semibold text-slate-900">£{result.vat.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t-2 border-emerald-300 flex justify-between items-center">
                  <span className="text-base font-bold text-emerald-900">Total (inc. VAT):</span>
                  <span className="text-3xl sm:text-4xl font-extrabold text-emerald-700" data-testid="calc-total">
                    £{result.total.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 flex items-start space-x-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p>
                This is an estimated price based on distance and vehicle type. Your final quote will be
                confirmed by our team within 24 hours after reviewing your booking details (including
                waiting time, multiple stops, or special handling).
              </p>
            </div>

            {onBookNow && (
              <Button
                onClick={onBookNow}
                size="lg"
                className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                data-testid="calc-book-now-btn"
              >
                Book This Delivery
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PriceCalculator;
